import { api } from '../api';

/**
 * Очередь закрытых точек на телефоне водителя.
 *
 * Связь в рейсе пропадает постоянно: подземный паркинг, двор-колодец, окраина.
 * Водитель не должен ждать сеть, стоя у двери клиента, поэтому закрытие точки
 * сначала ложится в localStorage, и только потом уходит на сервер.
 *
 * У каждого элемента есть `idempotencyKey`, созданный на телефоне один раз.
 * Сколько бы раз очередь ни отправилась повторно — проводка будет одна
 * (инвариант 6). Элемент вычёркивается, если сервер ответил «принято» ИЛИ
 * «уже закрыто»: для телефона это одинаково успешный исход.
 */

const KEY = 'watercrm.driver.queue';

export type QueueItem = {
  orderId: string;
  status: 'DONE' | 'PARTIAL' | 'FAILED';
  givenFull: number;
  takenEmpty: number;
  paidKop: string;
  paymentMethod: string;
  signature?: string | null;
  comment?: string | null;
  failReason?: string | null;
  idempotencyKey: string;
  clientTime: string;
  /** сколько раз пытались отправить — чтобы показать «не уходит» */
  attempts?: number;
  lastError?: string;
};

export function readQueue(): QueueItem[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '[]');
  } catch {
    return [];
  }
}

function writeQueue(items: QueueItem[]) {
  localStorage.setItem(KEY, JSON.stringify(items));
}

export function enqueue(item: QueueItem) {
  const q = readQueue();
  // Повторное закрытие той же точки заменяет предыдущую запись: водитель
  // исправился до отправки, и в очередь не должны попасть два разных факта
  const rest = q.filter((x) => x.orderId !== item.orderId);
  writeQueue([...rest, item]);
}

export function newIdempotencyKey(orderId: string): string {
  const rnd = crypto.randomUUID?.() ?? String(Math.random()).slice(2);
  return `drv-${orderId}-${rnd}`;
}

export type FlushResult = { sent: number; left: number; failed: QueueItem[] };

export async function flushQueue(): Promise<FlushResult> {
  const q = readQueue();
  if (!q.length) return { sent: 0, left: 0, failed: [] };

  const res = await api<{
    results: Array<{ orderId: string; ok: boolean; error?: string; message?: string; settled?: boolean }>;
  }>('/driver/sync', { body: { items: q.map(({ attempts, lastError, ...rest }) => ({ ...rest, attempts: undefined, lastError: undefined })) } });

  const settledIds = new Set(
    res.results.filter((r) => r.ok || r.settled).map((r) => r.orderId),
  );
  const failed = q
    .filter((item) => !settledIds.has(item.orderId))
    .map((item) => {
      const r = res.results.find((x) => x.orderId === item.orderId);
      return { ...item, attempts: (item.attempts ?? 0) + 1, lastError: r?.message };
    });

  writeQueue(failed);
  return { sent: settledIds.size, left: failed.length, failed };
}
